/**
 * @module SellingPlanPicker
 * @description Interactive selling plan selector for subscription products.
 * Provides radio-based plan selection with variant-aware price updates.
 * @requires @fc/component
 * @requires @fc/events
 * @customElement selling-plan-picker
 */
import { FCElement } from '@fc/component';
import { FCEvents } from '@fc/events';
import { patchDOM } from '@fc/dom-patch';
import { normalizeHTMLRoot } from '@fc/section-response';
import { formatFitMoney } from '@fc/money-formatting';

class SellingPlanPicker extends FCElement {
  requiredRefs = ['sellingPlanInput'];
  #ac = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    // Listen for radio changes within this element
    this.addEventListener('change', this.#onPlanChange, { signal });

    // Listen for variant changes in the parent section scope
    const scope = this.closest('.shopify-section, dialog, product-card');
    scope?.addEventListener(FCEvents.variantChange, this.#onVariantChange, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#ac.abort();
  }

  /**
   * Handle selling plan radio selection changes.
   * Updates the hidden form input and active label styling.
   * @param {Event} evt
   */
  #onPlanChange = (evt) => {
    const radio = evt.target;
    if (!radio || radio.name !== 'fit_selling_plan_choice') return;
    if (radio.disabled) return;

    this.#syncActiveState();
    this.#emitPriceChange(radio);
  };

  /**
   * Handle variant change events. Update displayed prices based on the
   * new variant's selling plan allocations.
   * @param {Event} evt
   */
  #onVariantChange = (evt) => {
    const variant = evt.detail?.resource ?? null;

    const previousPlanId = this.#selectedPlanId();
    const freshPicker = this.#resolveIncomingPicker(evt.detail?.data?.html);
    if (freshPicker instanceof Element) {
      patchDOM(this, freshPicker);
      this.onMorphSync?.();
      if (variant) {
        this.#restoreSelection(previousPlanId);
        queueMicrotask(() => this.#emitSelectedPriceChange(variant));
      } else {
        this.#disableForUnresolvedVariant();
      }
      return;
    }

    if (!variant) {
      this.#disableForUnresolvedVariant();
      return;
    }

    this.dataset.variantId = variant.id;

    // Update one-time purchase price
    const onetimeEl = this.querySelector('[data-ref="onetimePrice"]');
    if (onetimeEl && variant.price != null) {
      onetimeEl.textContent = this.#formatMoney(variant.price);
    }

    // Build allocation lookup from variant
    const allocMap = new Map();
    if (variant.selling_plan_allocations) {
      for (const alloc of variant.selling_plan_allocations) {
        const planId = alloc.selling_plan_id ?? alloc.plan_id ?? alloc.selling_plan?.id;
        if (planId != null) allocMap.set(String(planId), alloc);
      }
    }

    const hasAllocations = allocMap.size > 0;
    this.hidden = !hasAllocations;
    this.dataset.hasSellingPlans = hasAllocations ? 'true' : 'false';

    const onetimeLabel = this.querySelector('.fit-sp__option[data-selling-plan-id=""]');
    if (onetimeLabel instanceof HTMLElement) {
      onetimeLabel.dataset.price = String(variant.price ?? '');
      onetimeLabel.dataset.compareAtPrice = String(variant.compare_at_price ?? '');
      onetimeLabel.dataset.priceFormatted = this.#formatMoney(variant.price);
      onetimeLabel.dataset.compareAtPriceFormatted = variant.compare_at_price ? this.#formatMoney(variant.compare_at_price) : '';
      onetimeLabel.dataset.unitPriceHtml = '';
    }

    // Update each plan option's price and availability
    this.querySelectorAll('.fit-sp__option[data-selling-plan-id]').forEach((label) => {
      const planId = label.dataset.sellingPlanId;
      if (!planId) return; // one-time purchase option

      const alloc = allocMap.get(planId);
      const priceEl = label.querySelector('.fit-sp__option-price');
      const radio = label.querySelector('.fit-sp__radio');

      if (alloc) {
        // Variant supports this plan
        if (priceEl) priceEl.textContent = this.#formatMoney(alloc.per_delivery_price ?? alloc.price);
        label.dataset.price = String(alloc.price ?? '');
        label.dataset.compareAtPrice = String(alloc.compare_at_price ?? '');
        label.dataset.perDeliveryPrice = String(alloc.per_delivery_price ?? '');
        label.dataset.priceFormatted = this.#formatMoney(alloc.price);
        label.dataset.compareAtPriceFormatted = alloc.compare_at_price ? this.#formatMoney(alloc.compare_at_price) : '';
        label.dataset.perDeliveryPriceFormatted = this.#formatMoney(alloc.per_delivery_price ?? alloc.price);
        label.dataset.unitPriceHtml = alloc.unit_price_html || '';
        if (radio) radio.disabled = false;
        label.hidden = false;
        label.classList.remove('fit-sp__option--unavailable');
      } else {
        // Variant does not support this plan: keep it out of the active tab/selection path.
        if (radio) radio.disabled = true;
        label.hidden = true;
        label.classList.add('fit-sp__option--unavailable');
      }
    });

    const fieldset = this.querySelector('.fit-sp__fieldset');
    if (fieldset instanceof HTMLFieldSetElement) {
      fieldset.disabled = !hasAllocations;
      fieldset.hidden = !hasAllocations;
    }

    if (!hasAllocations) {
      const hiddenInput = this.#sellingPlanInput();
      if (hiddenInput) hiddenInput.value = '';
      queueMicrotask(() => {
        this.dispatchEvent(
          new CustomEvent(FCEvents.sellingPlanChange, {
            bubbles: true,
            detail: {
              productId: this.dataset.productId || null,
              variant,
              pricing: null,
            },
          }),
        );
      });
      return;
    }

    this.#restoreSelection(previousPlanId);
    queueMicrotask(() => this.#emitSelectedPriceChange(variant));
  };

  #emitPriceChange(radio, variant = null) {
    const label = radio?.closest?.('.fit-sp__option');
    if (!(label instanceof HTMLElement)) return;

    const planId = label.dataset.sellingPlanId || '';
    const pricing = {
      sellingPlanId: planId,
      sellingPlanName: planId ? label.dataset.sellingPlanName || '' : '',
      price: this.#numberOrNull(label.dataset.price),
      compareAtPrice: this.#numberOrNull(label.dataset.compareAtPrice),
      perDeliveryPrice: this.#numberOrNull(label.dataset.perDeliveryPrice),
      priceFormatted: label.dataset.priceFormatted || '',
      compareAtPriceFormatted: label.dataset.compareAtPriceFormatted || '',
      perDeliveryPriceFormatted: label.dataset.perDeliveryPriceFormatted || '',
      unitPriceHTML: label.dataset.unitPriceHtml || '',
      adjustmentLabel: label.dataset.adjustmentLabel || '',
    };

    this.dispatchEvent(
      new CustomEvent(FCEvents.sellingPlanChange, {
        bubbles: true,
        detail: {
          productId: this.dataset.productId || null,
          variant: variant || { id: this.dataset.variantId || null },
          pricing,
        },
      }),
    );
  }

  #sellingPlanInput() {
    const input = this.querySelector('[data-ref="sellingPlanInput"], input[name="selling_plan"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #selectedPlanId() {
    const checked = this.querySelector('.fit-sp__radio:checked');
    if (checked instanceof HTMLInputElement) return checked.value;

    return this.#sellingPlanInput()?.value || '';
  }

  #syncActiveState() {
    const checked = this.querySelector('.fit-sp__radio:checked');
    const selectedValue = checked instanceof HTMLInputElement ? checked.value : '';
    const hiddenInput = this.#sellingPlanInput();
    if (hiddenInput) hiddenInput.value = selectedValue;

    this.querySelectorAll('.fit-sp__option').forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      label.classList.toggle('fit-sp__option--active', label.dataset.sellingPlanId === selectedValue);
    });
  }

  #restoreSelection(preferredPlanId = '') {
    const preferred = this.#usableRadioForValue(preferredPlanId);
    const fallback = preferred || (this.#requiresSellingPlan() ? null : this.#usableRadioForValue(''));

    this.querySelectorAll('.fit-sp__radio').forEach((radio) => {
      if (radio instanceof HTMLInputElement) radio.checked = false;
    });

    if (fallback instanceof HTMLInputElement) {
      fallback.checked = true;
    }

    this.#syncActiveState();
  }

  #requiresSellingPlan() {
    return this.dataset.requiresSellingPlan === 'true';
  }

  #usableRadioForValue(value) {
    const radio = this.querySelector(`.fit-sp__radio[value="${CSS.escape(String(value ?? ''))}"]`);
    if (!(radio instanceof HTMLInputElement) || radio.disabled) return null;

    const label = radio.closest('.fit-sp__option');
    if (label instanceof HTMLElement && label.hidden) return null;

    return radio;
  }

  #emitSelectedPriceChange(variant = null) {
    const selectedRadio = this.querySelector('.fit-sp__radio:checked');
    if (selectedRadio instanceof HTMLInputElement) {
      this.#emitPriceChange(selectedRadio, variant);
      return;
    }

    this.dispatchEvent(
      new CustomEvent(FCEvents.sellingPlanChange, {
        bubbles: true,
        detail: {
          productId: this.dataset.productId || null,
          variant: variant || { id: this.dataset.variantId || null },
          pricing: null,
        },
      }),
    );
  }

  #disableForUnresolvedVariant() {
    this.dataset.hasSellingPlans = 'false';
    const fieldset = this.querySelector('.fit-sp__fieldset');
    if (fieldset instanceof HTMLFieldSetElement) {
      fieldset.disabled = true;
    }

    this.querySelectorAll('.fit-sp__radio').forEach((radio) => {
      if (radio instanceof HTMLInputElement) radio.disabled = true;
    });

    const hiddenInput = this.#sellingPlanInput();
    if (hiddenInput) hiddenInput.value = '';

    this.dispatchEvent(
      new CustomEvent(FCEvents.sellingPlanChange, {
        bubbles: true,
        detail: {
          productId: this.dataset.productId || null,
          variant: null,
          pricing: null,
        },
      }),
    );
  }

  #resolveIncomingPicker(payload) {
    const sourceRoot = normalizeHTMLRoot(payload);
    if (!sourceRoot || typeof sourceRoot.querySelectorAll !== 'function') return null;

    const candidates = Array.from(sourceRoot.querySelectorAll('selling-plan-picker'));
    if (!candidates.length) return null;

    const productId = this.dataset.productId;
    if (productId) {
      const match = candidates.find((candidate) => candidate instanceof HTMLElement && candidate.dataset.productId === productId);
      if (match) return match;
    }

    return candidates[0] ?? null;
  }

  #numberOrNull(value) {
    const parsed = Number.parseInt(String(value ?? ''), 10);
    return Number.isFinite(parsed) ? parsed : null;
  }

  #formatMoney(cents) {
    return formatFitMoney(cents, {
      scope: this.dataset.moneyScope || 'productPages',
    });
  }
}

if (!customElements.get('selling-plan-picker')) {
  customElements.define('selling-plan-picker', SellingPlanPicker);
}
